import { Button, Tooltip } from '@mantine/core'
import { IconAlertTriangle, IconLock, IconRefresh } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'
import { useEtlJobCatalog } from '../../hooks/useEtlJobs'
import type { EtlRunDetail } from '../../types/etlRuns'
import { rerunPlan } from './runFormat'
import type { CatalogState } from './runFormat'

/**
 * The Explorer's Rerun / Resume button.
 *
 * It does not run anything. It opens the Scheduler on the catalogue job that
 * loads this entity, with this run's recorded window filled in, and the
 * Scheduler — which shows the resume note and marks link-sourced values — is
 * where a request is made. So the button's whole job is to be honest about
 * what that request would do, and every word of that comes from the served
 * catalogue through `rerunPlan`:
 *
 *  - resume is on by default, so inside the window a request REOPENS a run;
 *    the label says "Resume this run" when that run is this one, and warns when
 *    it is another (resume matches on orchestrator and mode only);
 *  - one entity load runs at a time, and a request made during another is
 *    dropped, which every tooltip ends with.
 *
 * Disabled rather than hidden, with the reason as the tooltip, as JobsPage's
 * Run button is. `data-disabled` rather than `disabled`, because a disabled
 * button fires no pointer events and its tooltip would never show; the click
 * is refused in the handler instead.
 */
export function RerunAction({ run }: { run: EtlRunDetail }) {
  const navigate = useNavigate()
  const { data: catalog, isError } = useEtlJobCatalog()

  // A catalogue already in hand is used even if a later refetch failed: it is
  // the last thing the server said, and "unknown" would throw that away.
  const catalogState: CatalogState = catalog ? 'ready' : isError ? 'error' : 'loading'
  const plan = rerunPlan(run, catalog, catalogState)
  const disabled = plan.disabledReason !== null

  const icon = disabled
    ? <IconLock size={14} />
    : plan.warn
      ? <IconAlertTriangle size={14} color="var(--status-warning-fg)" />
      : <IconRefresh size={14} />

  return (
    <Tooltip label={plan.disabledReason ?? plan.note} multiline w={340} withArrow>
      <Button
        size="compact-sm"
        variant="default"
        leftSection={icon}
        data-disabled={disabled || undefined}
        aria-disabled={disabled || undefined}
        aria-label={`${plan.label}. ${plan.disabledReason ?? plan.note}`}
        onClick={(event) => {
          if (disabled || !plan.href) {
            event.preventDefault()
            return
          }
          navigate(plan.href)
        }}
      >
        {plan.label}
      </Button>
    </Tooltip>
  )
}
